import { Box, Button, Flex, Input, InputGroup, InputLeftElement, useBoolean } from '@chakra-ui/react';
import { ReactComponent as CopyLinkIcon } from 'assets/icons/share_link.svg';
import { DESIGN_PREVIEW } from 'constants/mixpanel';
import { useDetectPhishing } from 'hooks/useDetectPhishing/useDetectPhishing';
import { PhishingShareMethod } from 'hooks/useDetectPhishing/useDetectPhishing.config';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { PageThumbnail } from 'modules/Editor/components/PageManager/Slides/Slide';
import { useAppSelector } from 'modules/Editor/store';
import {
  selectHasFormWidgets,
  selectInfographHeightPx,
  selectInfographWidthPx,
  selectPageOrder,
} from 'modules/Editor/store/infographSelector';
import { useUser } from 'modules/User';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { isStandalone } from 'utils/environment';

import { UpgradeButton } from '../../../UpgradeButton';
import { PreviewButton } from '../../ResponsiveLinkPreview/ResponsiveLinkPreviewModal';
import { PreviewNavbar } from '../../ResponsiveLinkPreview/ResponsivePreview';
import { InteractionSubmission } from '../InteractionSubmission';

type SharePrivateLinkProps = {
  onCopyLink?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
};

export const SharePrivateLinkTab = ({ onCopyLink = () => {} }: SharePrivateLinkProps) => {
  const { t } = useTranslation('common_navbar');
  const { id } = useParams();
  const { detectPhishing, phishingToast } = useDetectPhishing();
  const [isLoading, setLoading] = useBoolean();
  const { venngageE2 } = useVenngageE1Gate();
  const widthPx = useAppSelector(selectInfographWidthPx);
  const heightPx = useAppSelector(selectInfographHeightPx);
  const pageOrder = useAppSelector(selectPageOrder);
  const hasForms = useAppSelector(selectHasFormWidgets);
  const { userType, isFreeUser, isPremiumUser, isGuestRole, isBusinessUser, isEnterpriseUser } = useUser();

  const disabled = isFreeUser || isGuestRole;

  const privateLinkURL = useMemo(() => {
    const shareLinkId = isStandalone ? id : venngageE2?.privateLinkId;
    const fakeId = '123456abc'; // This fake ID is the same with the e1
    const path = 'pl';
    const result = new URL(`${window.location.origin}/${path}/${disabled ? fakeId : shareLinkId}`).toString();

    return result;
  }, [disabled, id, venngageE2]);

  const showInteractionSubmission =
    hasForms && (isBusinessUser || isEnterpriseUser) && !!venngageE2?.experiments?.unGatedInteractionSubmission;

  const openPrivateLink = async () => {
    if (isLoading) return;

    setLoading.on();
    const phishing = await detectPhishing(PhishingShareMethod.Preview);
    setLoading.off();

    if (phishing?.phishingDetected) {
      phishingToast({ description: t('phishing.message'), title: t('phishing.previewTitle') });
      return;
    }

    window.open(privateLinkURL, '_blank');

    Mixpanel.track(DESIGN_PREVIEW.eventName, {
      from: DESIGN_PREVIEW.from.shareButton,
    });
  };

  const sharePrivateLinkTestId = 'share-private-link';
  const sharePrivateLinkInputTestId = 'share-private-link-copy-link-input';
  const sharePrivateLinkCopyLinkTestId = 'share-private-link-copy-link-button';
  const label = t('modals.share-panel.responsive-share.preview');

  return (
    <Flex flexDir='column' gap='2' data-testid={sharePrivateLinkTestId}>
      <Flex
        background='gray.50'
        borderRadius='md'
        data-testid={'share-preview'}
        align='center'
        justify='center'
        py={6}
        mb={2}
        direction='column'
        position='relative'
      >
        <Box shadow='base' borderRadius='md'>
          <PreviewNavbar fullScreenScale={2} isMobile={false} />
          <Box background='gray.800' py={2} px={14} borderBottomRadius='md'>
            <PageThumbnail
              isActivePage={false}
              infographSize={{ widthPx, heightPx }}
              isDragging={false}
              pageId={pageOrder[0]}
              thumbnailCss={{ mr: '0', ml: '0', border: '2px solid var(--vg-colors-gray-800)', background: 'gray.800' }}
            />
          </Box>
        </Box>
        <Box position='absolute' bottom={2} right={2}>
          <PreviewButton label={label} onClick={openPrivateLink} disabled={disabled} />
        </Box>
      </Flex>
      {showInteractionSubmission && (
        <Flex mb='2'>
          <InteractionSubmission />
        </Flex>
      )}
      <Flex gap='2' direction='column'>
        <Box w='full'>
          <InputGroup>
            <InputLeftElement pointerEvents='none' pl='2' width='32px' height='32px' color='font.300'>
              <CopyLinkIcon width='20px' height='20px' />
            </InputLeftElement>
            <Input
              isReadOnly
              size='sm'
              textOverflow={'ellipsis'}
              disabled={disabled}
              value={privateLinkURL}
              data-testid={sharePrivateLinkInputTestId}
              borderRadius={'base'}
            />
          </InputGroup>
        </Box>
        {!disabled && (
          <Button
            onClick={onCopyLink}
            disabled={disabled}
            variant='action'
            size='sm'
            width='full'
            data-testid={sharePrivateLinkCopyLinkTestId}
          >
            {t('modals.share-panel.sharing.copy-private-link')}
          </Button>
        )}
        {!isPremiumUser && (
          <UpgradeButton
            userType={userType}
            text={t('modals.share-panel.sharing.upgrade-to-share')}
            isGuestRole={isGuestRole}
            renderFrom='share-panel'
            showIcon
          />
        )}
      </Flex>
    </Flex>
  );
};
